import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from "recharts";
import { BarChart3, AudioWaveform, Activity, ImageIcon } from "lucide-react";

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-2">
      <p className="text-sm font-bold uppercase tracking-wider text-slate-400">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-sm font-mono" style={{ color: p.color }}>
          {p.name}: {typeof p.value === 'number' ? p.value.toFixed(3) : p.value}
        </p>
      ))}
    </div>
  );
};

export default function SoundHistogram({ data, histogramImage, waveformImage }) {
  if (!data) return null;

  const histogram = data.histogram || [];
  const waveform = data.waveform || [];
  const timeSeries = data.time_series || [];
  const stats = data.stats || {};

  return (
    <Card className="border border-slate-200 shadow-none rounded-sm overflow-hidden" data-testid="sound-histogram-card">
      <div className="border-b border-slate-200 p-3 bg-slate-50/50 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BarChart3 size={14} className="text-slate-500" />
          <span className="text-sm font-bold uppercase tracking-wider text-slate-500">Sound Analysis</span>
        </div>
        <div className="flex items-center gap-4">
          {stats.duration && <span className="text-sm font-mono text-slate-400">Duration: {stats.duration}s</span>}
          {stats.peak_frequency && <span className="text-sm font-mono text-slate-400">Peak: {stats.peak_frequency} Hz</span>}
          {stats.avg_energy && <span className="text-sm font-mono text-slate-400">Avg Energy: {stats.avg_energy}</span>}
        </div>
      </div>
      <CardContent className="p-4">
        <Tabs defaultValue={histogramImage ? "analysis-image" : "histogram"} className="w-full">
          <TabsList className="bg-slate-100 rounded-sm h-8 mb-4">
            {histogramImage && (
              <TabsTrigger value="analysis-image" className="text-sm font-bold uppercase tracking-wider rounded-sm data-[state=active]:bg-white" data-testid="tab-analysis-image">
                <ImageIcon size={12} className="mr-1" /> Analysis Report
              </TabsTrigger>
            )}
            <TabsTrigger value="histogram" className="text-sm font-bold uppercase tracking-wider rounded-sm data-[state=active]:bg-white" data-testid="tab-histogram">
              <BarChart3 size={12} className="mr-1" /> Frequency
            </TabsTrigger>
            <TabsTrigger value="waveform" className="text-sm font-bold uppercase tracking-wider rounded-sm data-[state=active]:bg-white" data-testid="tab-waveform">
              <AudioWaveform size={12} className="mr-1" /> Waveform
            </TabsTrigger>
            <TabsTrigger value="energy" className="text-sm font-bold uppercase tracking-wider rounded-sm data-[state=active]:bg-white" data-testid="tab-energy">
              <Activity size={12} className="mr-1" /> Energy
            </TabsTrigger>
          </TabsList>

          {/* NEW: Analysis Image Tab */}
          {histogramImage && (
            <TabsContent value="analysis-image">
              <div className="relative">
                <img 
                  src={`data:image/png;base64,${histogramImage}`} 
                  alt="Engine Sound Frequency Analysis"
                  className="w-full rounded-md border border-slate-200"
                  data-testid="histogram-image"
                />
                <div className="absolute top-2 right-2 bg-emerald-500 text-white text-sm font-bold px-2 py-1 rounded-sm uppercase">
                  AI Verified
                </div>
              </div>
              {waveformImage && (
                <div className="mt-4">
                  <img 
                    src={`data:image/png;base64,${waveformImage}`} 
                    alt="Audio Waveform Analysis"
                    className="w-full rounded-md border border-slate-200"
                    data-testid="waveform-image"
                  />
                </div>
              )}
              <p className="text-sm text-slate-400 mt-3 text-center">
                Generated frequency analysis showing sound distribution across frequency bands
              </p>
            </TabsContent>
          )}

          <TabsContent value="histogram">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={histogram}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis
                  dataKey="freq_center"
                  tick={{ fontSize: 10, fill: '#94A3B8' }}
                  tickFormatter={(v) => `${v}Hz`}
                  interval={4}
                />
                <YAxis tick={{ fontSize: 10, fill: '#94A3B8' }} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="magnitude" fill="#0EA5E9" radius={[1, 1, 0, 0]} name="Magnitude" />
              </BarChart>
            </ResponsiveContainer>
          </TabsContent>

          <TabsContent value="waveform">
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={waveform}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis
                  dataKey="time"
                  tick={{ fontSize: 10, fill: '#94A3B8' }}
                  tickFormatter={(v) => `${v}s`}
                />
                <YAxis tick={{ fontSize: 10, fill: '#94A3B8' }} domain={[0, 1]} />
                <Tooltip content={<CustomTooltip />} />
                <Area
                  type="monotone"
                  dataKey="amplitude"
                  stroke="#0F172A"
                  fill="#0EA5E9"
                  fillOpacity={0.15}
                  strokeWidth={1.5}
                  name="Amplitude"
                />
              </AreaChart>
            </ResponsiveContainer>
          </TabsContent>

          <TabsContent value="energy">
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={timeSeries}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis
                  dataKey="time"
                  tick={{ fontSize: 10, fill: '#94A3B8' }}
                  tickFormatter={(v) => `${v}s`}
                />
                <YAxis tick={{ fontSize: 10, fill: '#94A3B8' }} />
                <Tooltip content={<CustomTooltip />} />
                <Line type="monotone" dataKey="energy" stroke="#F97316" strokeWidth={1.5} dot={false} name="Energy" />
                <Line type="monotone" dataKey="centroid" stroke="#0EA5E9" strokeWidth={1} dot={false} name="Centroid (Hz)" />
              </LineChart>
            </ResponsiveContainer>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
